import React, { memo, MouseEvent, useCallback, useMemo } from 'react'
import { format } from 'date-fns'

import Image from '../image/image'
import Svg from '../svg/svg'
import { IconButton, StackedProgressBar, Tooltip } from '..'

import { ChildProgressBar } from '../stackedProgressBar/types'
import { calculatePercentage } from '../stackedProgressBar/helpers'
import { formatAmount } from '../../../utils/functions'
import { AuditStatus } from '../../views/audits/allAudits/enums'

import * as styles from './styles.module.scss'
import { AuditCardProps } from './types'
import InfoTooltip from '../infoTooltip/infoTooltip'

const AuditCard = ({
   audit,
   onCardClick,
   onDeleteClick,
   onExportClick,
   onReviewedClick,
   isEmployeeUser,
}: AuditCardProps) => {
   const title = useMemo(
      () => (audit?.AuditedBy?.length > 0 ? audit.AuditedBy : '-'),
      [audit.AuditedBy]
   )

   const isDraft = useMemo(
      () => audit.Status === AuditStatus.DRAFT,
      [audit.Status]
   )

   const handleCardEditClick = useCallback(
      (e: MouseEvent<HTMLElement>) => onCardClick(String(audit.AuditID), e),
      [onCardClick, audit.AuditID]
   )

   const handleExportClick = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
         e.stopPropagation()
         onExportClick(String(audit.AuditID))
      },
      [onExportClick, audit.AuditID]
   )

   const handleDeleteClick = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
         e.stopPropagation()
         onDeleteClick(String(audit.AuditID))
      },
      [onDeleteClick, audit.AuditID]
   )

   const handleReviewedClick = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
         e.stopPropagation()
         onReviewedClick(String(audit.AuditID))
      },
      [onReviewedClick, audit.AuditID]
   )

   const progressBars: ChildProgressBar[] = useMemo(() => {
      const { ItemTotal, ItemsConfirmed, ItemsNotFoundInAudit } = audit
      return [
         {
            variant: 'success',
            percentage: calculatePercentage(ItemTotal, ItemsConfirmed),
         },
         {
            variant: 'warning',
            percentage: calculatePercentage(ItemTotal, ItemsNotFoundInAudit),
         },
      ]
   }, [audit.ItemTotal, audit.ItemsConfirmed, audit.ItemsNotFoundInAudit])

   const formatAuditDate = (d?: string | null) =>
      d ? format(new Date(d), 'd MMM yyyy') : '-'

   return (
      <article
         className={styles.auditCard}
         role="button"
         tabIndex={0}
         aria-label={`Open Audit ${audit?.Title}`}
         onClick={handleCardEditClick}
      >
         <StackedProgressBar
            bars={progressBars}
            className={styles.progressBar}
         />
         <header>
            <div className={styles.imageAndTitle}>
               <Image
                  url={
                     audit?.PictureURL && audit.PictureURL !== ''
                        ? audit.PictureURL
                        : '/images/connection-placeholder.svg'
                  }
                  alt={`Picture of ${title}`}
                  width={24}
                  height={24}
                  isCircle
               />
               <h2>{title}</h2>
            </div>
            <div className={styles.totalItems}>
               <h2>{formatAmount(audit?.ItemTotal, 'Item')}</h2>
            </div>
         </header>
         <section>
            <div className={styles.contentBox}>
               <div>
                  <Svg id="audit-sent" width={14} height={13} />
                  {isDraft ? (
                     <p>-</p>
                  ) : (
                     <p>Sent {formatAuditDate(audit?.DateSent)}</p>
                  )}
               </div>
               <div>
                  <Svg id="audit-due" width={14} height={14} />
                  {isDraft ? (
                     <p>-</p>
                  ) : (
                     <p>Due {formatAuditDate(audit?.DateDue)}</p>
                  )}
               </div>
               <div>
                  <Svg id="audit-received" width={14} height={13} />
                  {isDraft ? (
                     <p>-</p>
                  ) : (
                     <p>Received {formatAuditDate(audit?.DateReceived)}</p>
                  )}
               </div>
            </div>
            <div
               className={`${styles.contentBox} ${
                  isDraft ? styles.draftItems : ''
               }`}
            >
               <div>
                  <div className={`${styles.circle} ${styles.confirmed}`} />
                  <p>{audit?.ItemsConfirmed} Confirmed</p>
               </div>
               <div>
                  <div className={styles.circle} />
                  <p>{audit?.ItemsUnConfirmed} Unconfirmed</p>
               </div>
               <div>
                  <div className={`${styles.circle} ${styles.found}`} />
                  <p>{audit?.ItemsNotFoundInAudit} Found</p>
               </div>
            </div>
         </section>
         <footer>
            
            <div className={styles.textContainer}>
               <div className={styles.titleIconContainer}>
                  {audit?.AuditRuleName && (
                     <InfoTooltip 
                        content={`Created by Smart Audit Rule: ${audit.AuditRuleName}`} 
                        openIcon={'repeating-contract-filled'} 
                        closedIcon={'repeating-contract'} 
                        ariaLabel={`Created by smart audit rule ${audit.AuditRuleName}`}
                        inlineStyle={{marginLeft: '-1rem', marginRight: '1rem'}}
                     />
                  )}
                  <p>{audit?.Title}</p>
               </div>
               <p>{audit?.str_Status}</p>
            </div>
            {!isEmployeeUser && (
               <div className={styles.buttonsContainer}>
                  <Tooltip content="Edit">
                     <IconButton
                        id="edit-outlined"
                        action={handleCardEditClick}
                     />
                  </Tooltip>
                  <Tooltip content="Export to Excel">
                     <IconButton id="excel-export" action={handleExportClick} />
                  </Tooltip>
                  {audit.Status === AuditStatus.FOR_REVIEW ? ( 
                     <Tooltip content="Reviewed">
                        <IconButton
                           id="reviewed-outlined"
                           action={handleReviewedClick}
                        />
                     </Tooltip>
                  ) : (
                     <Tooltip content="Delete">
                        <IconButton
                           id="delete-outlined"
                           action={handleDeleteClick}
                        />
                     </Tooltip>
                  )
                  }
               </div>
            )}
         </footer>
      </article>
   )
}

export default memo(AuditCard)
